import { FunctionComponent, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import cn from 'classnames';
import { FormSpec, Person, Requirement } from '../../types';
import {
  Button,
  NumberField,
  Section,
  SectionHeading,
  Confirmation,
} from '../../components';
import { cleanIntakeDates } from '../../utils/intake';
import { successCallbackPath } from '../../utils/paths';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { Renderer, formSpecToRendererConfig } from '../../components/forms';
import DelawareAnnualReportSpec from '../../components/forms/specs/de_annual_report_spec.json';
import { useNavigate } from 'react-router-dom';
import { FormError } from '../../components/forms/FormError';
import { useRequirementFee } from '../../hooks/useRequirementFee';
import { RequirementFeeText } from '../../components/RequirementFeeText';

const StateSpecMapping: Record<string, FormSpec> = {
  de: DelawareAnnualReportSpec,
};

type DelawareAnnualReportProps = {
  locationId: string;
  requirement: Requirement;
  peopleData: Person[];
  callbackUrl?: string | null;
};

export const DelawareAnnualReport: FunctionComponent<
  DelawareAnnualReportProps
> = ({ locationId, requirement, peopleData, callbackUrl }) => {
  const fee = useRequirementFee(requirement);
  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);
  const [hasEstimateError, setHasEstimateError] = useState<boolean>(false);
  const [isSubmitSuccessful, setSubmitSuccessful] = useState<boolean>(false);
  const [estimateTaxValue, setEstimateTaxValue] = useState<null | string>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  type AnnualReportIFormValue = {
    franchise_tax_estimate: number;
    has_preferred_stock: boolean;
    total_authorized_shares_common: number;
    total_authorized_shares_preferred: number;
    total_issued_shares_common: number;
    total_issued_shares_preferred: number;
    par_value_common: number;
    annual_report_year: string;
    par_value_preferred: number;
    gross_assets: number;
    fiscal_year_end_date: Date;
    has_made_stock_amendments: boolean;
    stock_amendments?: {
      amendment_date: Date;
      total_authorized_shares_common: number;
      total_authorized_shares_preferred: number;
      total_issued_shares_common: number;
      total_issued_shares_preferred: number;
      par_value_common: number;
      par_value_preferred: number;
      gross_assets: number;
    }[];
    additional_officer_info: {
      corporate_role: string;
    };
    directors: {
      first_name: string;
      last_name: string;
    }[];
    agree_tc_and_authorized: boolean;
  };

  const navigate = useNavigate();

  const formMethods = useForm<AnnualReportIFormValue>();
  const {
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger: triggerValidation,
    formState: { errors },
  } = formMethods;

  const formIsRuntimeValid = (): boolean => {
    clearErrors();
    let valid = true;
    if (locationId === 'de') {
      const [annualReportYear, fiscalYearEndDate]: [string, Date] = getValues([
        'annual_report_year',
        'fiscal_year_end_date',
      ]);
      // check that years are the same
      const fiscalYear = fiscalYearEndDate.getFullYear();
      if (
        fiscalYear &&
        annualReportYear &&
        fiscalYear.toString() !== annualReportYear
      ) {
        setError(
          'fiscal_year_end_date',
          {
            type: 'manual',
            message:
              'Fiscal year end date should be within annual reporting year',
          },
          {
            shouldFocus: true,
          }
        );
        valid = false;
      }
    }
    return valid;
  };

  const calculateEstimate = async () => {
    setHasEstimateError(false);
    setIsCalculating(true);

    const isTriggerValid = await triggerValidation();
    const isRuntimeValid = formIsRuntimeValid();
    if (!isRuntimeValid || !isTriggerValid) {
      setIsCalculating(false);
      if (isRuntimeValid && !isTriggerValid) {
        // Object.keys returns string[] by design
        // We need to use type assertion
        const err = Object.keys(errors)[0] as keyof AnnualReportIFormValue;

        // focus first error
        await triggerValidation(err, { shouldFocus: true });
      }
      return;
    }

    const [
      totalIssuedSharesCommon,
      totalAuthorizedSharesCommon,
      parValueCommon,
      grossAssets,
    ] = getValues([
      'total_issued_shares_common',
      'total_authorized_shares_common',
      'par_value_common',
      'gross_assets',
    ]);

    // call legalinc proxied endpoint
    const estimateResponse = await fetchApi({
      url: `/api/legal_entity/locations/${locationId}/annual_report/estimate`,
      method: 'POST',
      body: {
        authorized_shares: totalAuthorizedSharesCommon,
        issued_shares: totalIssuedSharesCommon,
        par_value: parValueCommon,
        gross_assets: grossAssets,
      },
    });

    if (estimateResponse.error) {
      setHasEstimateError(true);
      setIsCalculating(false);
      return;
    }

    // if success:
    const taxValue = estimateResponse.data.estimate;
    setValue('franchise_tax_estimate', taxValue);
    setEstimateTaxValue(
      // format with commas 1000 -> 1,000
      taxValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    );
    setIsCalculating(false);
  };

  const onSubmit = (data: any) => {
    setHasSubmitError(false);

    const cleanedData = cleanIntakeDates(data);
    const filingYear = getValues('annual_report_year');

    const payload = {
      data: cleanedData,
      legal_entity_requirement_id: `${requirement.id}`,
      data_key: `${locationId}_annual_report_${filingYear}`,
    };

    fetchApi({
      // realized this might invalidate the submitUrl field on the spec?
      // todo: decide which approach is better...
      url: `/api/legal_entity/locations/de/annual_report`,
      method: 'POST',
      body: payload,
    }).then(({ status: status1 }: IApiData) => {
      if (status1 === ApiStatus.Error) {
        setHasSubmitError(true);
      } else {
        // Set the requirement to managed and in-progress
        fetchApi({
          url: `/api/requirements/${requirement.id}`,
          method: 'PATCH',
          body: {
            operation: 'mark-as-managed',
          },
        }).then(({ status: status2 }: IApiData) => {
          if (status2 === ApiStatus.Error) {
            setHasSubmitError(true);
          } else {
            if (callbackUrl) {
              navigate(successCallbackPath(callbackUrl), { replace: true });
              return;
            }

            setSubmitSuccessful(true);
          }
        });
      }
    });
  };

  const loadedSpec = StateSpecMapping[locationId];

  return (
    <Section className="flex flex-col items-center">
      {isSubmitSuccessful ? (
        <Confirmation
          heading="Success"
          description="Your request is on its way! Once this is processed, we'll send you a final invoice for your tax payment."
        />
      ) : (
        <>
          <SectionHeading className="w-96 pb-4" text={loadedSpec.title} />
          <FormProvider {...formMethods}>
            <form className="my-8 w-96" onSubmit={handleSubmit(onSubmit)}>
              <Renderer
                config={formSpecToRendererConfig(loadedSpec)}
                errors={errors}
                peopleData={peopleData}
              />
              <div className="border bg-gray-50 px-8 py-4">
                <label className="mb-2 mt-4 block font-semibold">
                  Franchise Tax Estimate
                </label>
                <p className="text-sm text-gray-600">
                  This is estimated using the Authorized Shares method for
                  common stock based on the total number of shares authorized in
                  your Certificate of Incorporation. Other situations including
                  amendments and multiple classes of stock may make this
                  estimate less accurate. An invoice will be sent for your tax
                  payment plus any additional fees.
                </p>
                <NumberField
                  name="franchise_tax_estimate"
                  label=""
                  error={errors.franchise_tax_estimate}
                  className="hidden"
                  isCurrency
                  disabled
                />
                <p
                  className={cn('my-4 text-lg font-semibold', {
                    invisible: !estimateTaxValue,
                  })}
                >
                  ${estimateTaxValue}
                </p>
                <p className="mb-4 flex w-full flex-col">
                  <Button
                    type="button"
                    onClick={calculateEstimate}
                    isLoading={isCalculating}
                    variant={estimateTaxValue ? 'secondary' : 'primary'}
                  >
                    {estimateTaxValue
                      ? 'Recalculate Estimate'
                      : 'Calculate Estimate'}
                  </Button>
                  {hasEstimateError && <FormError margin="mt-2" />}
                </p>
              </div>
              <p className="mt-8 flex flex-col">
                <Button type="submit" disabled={!estimateTaxValue} size="large">
                  Submit
                </Button>
                {hasSubmitError && <FormError />}
                {fee && (
                  <div className="mt-4">
                    <RequirementFeeText fee={fee} />
                  </div>
                )}
              </p>
            </form>
          </FormProvider>
        </>
      )}
    </Section>
  );
};
